import { compositionTypeCodeSummaryOfNetEffect } from "../../dictionaries/CodeSystemLookup";
import getIdFromReference from "../../functions/getIdFromReference";
import addElementValuesForSummaryOfFindingsFromComparativeEvidenceSynthesisReport from "./addElementValuesForSummaryOfFindingsFromComparativeEvidenceSynthesisReport";
import { checkIfStatisticAbsoluteOrRelative, codeMatchesPercentage, codeMatchesProportion, codeMatchesRelativeRisk, codeMatchesRiskDifference } from "./summaryOfFindingsFunctions";

const columnHeadersSection = {
    'title': 'Column Headers',
    'code': { 'coding': [{ 'system': 'https://fevir.net/resources/CodeSystem/179423', 'code': 'column-headers', 'display': 'Column Headers' }] },
    'section': [
        {
            "title": "Outcome",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "outcome-measure",
                        "display": "Outcome Measure"
                    }
                ]
            },
            "text": {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Outcome</p></div>"
            }
        },
        {
            "title": "Desirability",
            "code": {
                'coding': [{
                    "system": "https://fevir.net/resources/CodeSystem/179423",
                    "code": "outcome-desirability",
                    "display": "Outcome Desirability"
                }],
                "text": "Outcome Desirability"
            },
            "text": {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Desirability</p></div>"
            }
        },
        {
            "title": "Relative Importance",
            "code": {
                'coding': [{
                    "system": "https://fevir.net/resources/CodeSystem/179423",
                    "code": "relative-importance",
                    "display": "Relative Importance"
                }],
                "text": "Relative Importance"
            },
            "text": {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Relative Importance</p></div>"
            }
        },
        {
            "title": "Result Without Treatment",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "result-with-comparator-alone",
                        "display": "Result with comparator alone"
                    }
                ]
            },
            "text": {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result Without Treatment</p></div>"
            }
        },
        {
            "title": "Result With Treatment--Derived",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "result-with-intervention-alone-calculated",
                        "display": "Result with intervention alone (calculated)"
                    }
                ],
                "text": "result with intervention with calculated value derived from the control group and effect estimate"
            },
            "text": {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result With Treatment (Calculated)</p></div>"
            }
        },
        {
            "title": "Effect Estimate",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "result-with-intervention-vs-comparator",
                        "display": "Result with intervention vs. comparator"
                    }
                ]
            },
            "text": {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Difference with Treatment (Confidence Interval)</p></div>"
            }
        },
        {
            "title": "Net Effect Contribution",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "net-effect-contribution",
                        "display": "Net Effect Contribution"
                    }
                ],
                "text": "Net Effect Contribution"
            },
            "text": {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Health Impact (Confidence Interval)</p></div>"
            }
        }
    ]
};

const addElementValuesForSummaryOfNetEffectFromComparativeEvidenceSynthesisReport = (resourceJson, resourceReferenceMap,
    associatedOutcomeMeasureReportState) => {
    if (!resourceJson) {
        return null;
    }
    let addElementValues = addElementValuesForSummaryOfFindingsFromComparativeEvidenceSynthesisReport(resourceJson,
        resourceReferenceMap, associatedOutcomeMeasureReportState);
    addElementValues.type = compositionTypeCodeSummaryOfNetEffect;
    let newSectionForComposition = [columnHeadersSection,
        {
            'title': 'Summary of Findings',
            'code': { 'coding': [{ 'system': 'https://fevir.net/resources/CodeSystem/179423', 'code': 'summary-of-findings', 'display': 'Summary of Findings' }] },
            'section': []
        },
        {
            'title': 'Net Effect Estimate',
            'code': {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "net-effect-estimate",
                        "display": "Net Effect Estimate"
                    }
                ],
                'text': 'Net Effect Estimate'
            },
            'text': { 'status': 'empty', 'div': "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[Not yet calculated.]</p></div>" },
        }
    ];
    let resourceids = [];
    let targetoutcomeids = [];
    let sourceFoiDictionary = {};
    let mapSourceFoiToTargetOutcomeIdDictionary = {};
    let resourceDictionary = resourceReferenceMap?.combinedDictionary || {};
    let evidenceDictionary = associatedOutcomeMeasureReportState?.resourceDictionary || {};
    let summaryOfFindingsSection = [];

    for (const section of addElementValues.section) {
        if (section?.code?.coding?.[0]?.code === "summary-of-findings" &&
            section.section.length > 0
        ) {
            summaryOfFindingsSection = section.section;
        }
    }
    for (const section of summaryOfFindingsSection) {
        if (section.focus) {
            let outcomeFoi = getIdFromReference(section.focus);
            if (outcomeFoi) {
                targetoutcomeids.push(outcomeFoi);
            }
            sourceFoiDictionary[outcomeFoi] = {
                "targetOutcome": null,
                "comparatorOnlyEvidence": null,
                "comparativeEvidence": null
            };
            if (section.section?.length) {
                for (const subsection of section.section) {
                    if (subsection.entry?.length) {
                        let sectionCode = subsection.code?.coding?.[0]?.code || subsection.code?.text;
                        for (const entry of subsection.entry) {
                            let foi = getIdFromReference(entry);
                            if (foi) {
                                resourceids.push(foi);
                                if (sectionCode === "outcome-measure" || sectionCode === "EvidenceVariable-outcome") {
                                    sourceFoiDictionary[outcomeFoi]["targetOutcome"] = evidenceDictionary[foi] || resourceDictionary[foi];
                                }
                                if (sectionCode === "result-with-comparator-alone" || sectionCode === "Control-group-alone-Evidence" || sectionCode === "evidence-with-comparator-alone") {
                                    sourceFoiDictionary[outcomeFoi]["comparatorOnlyEvidence"] = evidenceDictionary[foi] || resourceDictionary[foi];
                                }
                                if (sectionCode === "result-with-intervention-vs-comparator" || sectionCode === "Intervention-vs-Control-Evidence" || sectionCode === "evidence-with-intervention-vs-comparator") {
                                    sourceFoiDictionary[outcomeFoi]["comparativeEvidence"] = evidenceDictionary[foi] || resourceDictionary[foi];
                                }
                                mapSourceFoiToTargetOutcomeIdDictionary[foi] = outcomeFoi;
                            }
                        }
                    }
                }
            }
        }
    }

    for (const targetOutcomeFoi of targetoutcomeids) {
        let outcomeDefinitionJson = sourceFoiDictionary[targetOutcomeFoi]["targetOutcome"];
        if (outcomeDefinitionJson) {
            let comparatorOnlyEvidenceJson;
            if (sourceFoiDictionary[targetOutcomeFoi]["comparatorOnlyEvidence"]) {
                comparatorOnlyEvidenceJson = sourceFoiDictionary[targetOutcomeFoi]["comparatorOnlyEvidence"];
            }
            let comparativeEvidenceJson;
            if (sourceFoiDictionary[targetOutcomeFoi]["comparativeEvidence"]) {
                comparativeEvidenceJson = sourceFoiDictionary[targetOutcomeFoi]["comparativeEvidence"];
            }
            let outcomeTitle = outcomeDefinitionJson.title || outcomeDefinitionJson.name;
            let outcomeDisplayName = outcomeTitle.replace("OutcomeDefinition: ", "");
            let outcomeSection = {
                'title': outcomeDisplayName,
                'code': {
                    "coding": [
                        {
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "summary-of-net-effect-contributions-entry-for-a-single-outcome",
                            "display": "Summary of net effect contributions entry for a single outcome"
                        }
                    ],
                    'text': 'Summary of net effect contributions entry for a single outcome'
                },
                'focus': {
                    'reference': 'EvidenceVariable/' + outcomeDefinitionJson.id,
                    'type': 'EvidenceVariable',
                    'display': outcomeTitle
                },
                'section': []
            };
            let outcomeNameSection = {
                "title": "Outcome",
                "code": {
                    "coding": [
                        {
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "outcome-measure",
                            "display": "Outcome Measure"
                        }
                    ]
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + outcomeDisplayName + "</p></div>"
                },
                "entry": [{
                    'reference': 'EvidenceVariable/' + outcomeDefinitionJson.id,
                    'type': 'EvidenceVariable',
                    'display': outcomeTitle
                }]
            };
            outcomeSection.section.push(outcomeNameSection);
            let desirabilitySection = {
                "title": "Desirability",
                "code": {
                    'coding': [{
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "outcome-desirability",
                        "display": "Outcome Desirability"
                    }],
                    "text": "Outcome Desirability"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[No data.]</p></div>"
                }
            }
            outcomeSection.section.push(desirabilitySection);
            let relativeImportanceSection = {
                "title": "Relative Importance",
                "code": {
                    'coding': [{
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "relative-importance",
                        "display": "Relative Importance"
                    }],
                    "text": "Relative Importance"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[No data.]</p></div>"
                }
            }
            outcomeSection.section.push(relativeImportanceSection);
            let controlEventRate = NaN;
            let controlEventRateDisplay = '[No statistic found.]'
            let comparatorOnlyEvidenceJsonEntry = null;
            if (comparatorOnlyEvidenceJson) {
                let Evidence = comparatorOnlyEvidenceJson;
                comparatorOnlyEvidenceJsonEntry = {
                    'reference': 'Evidence/' + Evidence.id,
                    'type': 'Evidence',
                    'display': (Evidence.title || Evidence.name || "")
                };
                if (Evidence.statistic && Evidence.statistic.length > 0) {
                    controlEventRateDisplay = "[Not a proportion.]"
                    for (const stat of Evidence.statistic) {
                        if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0) {
                            if (codeMatchesProportion(stat.statisticType.coding[0].code)) {
                                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                    controlEventRate = stat.quantity.value * 100;
                                    if (stat.quantity.unit === "%" || stat.quantity.code === "%") {
                                        controlEventRate = stat.quantity.value;
                                    }
                                    controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                                    break;
                                }
                            }
                            if (codeMatchesPercentage(stat.statisticType.coding[0].code)) {
                                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                    controlEventRate = stat.quantity.value;
                                    controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                                    break;
                                }
                            }
                            if (stat.statisticType.coding[0].code === 'STATO:0000047') {
                                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value) && stat.sampleSize) {
                                    let denominator = stat.sampleSize.numberAnalyzed || stat.sampleSize.knownDataCount || stat.sampleSize.numberOfParticipants || 0;
                                    if (denominator > 0) {
                                        controlEventRate = (stat.quantity.value / denominator) * 100;
                                        controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                                        break;
                                    }
                                }
                            }
                        }
                    }
                }
            }
            let controlResultSection = {
                "title": "Result Without Treatment",
                "code": {
                    "coding": [
                        {
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "result-with-comparator-alone",
                            "display": "Result with comparator alone"
                        }
                    ]
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + controlEventRateDisplay + "</p></div>"
                }
            };
            if (comparatorOnlyEvidenceJsonEntry) {
                controlResultSection["entry"] = [comparatorOnlyEvidenceJsonEntry];
            }
            outcomeSection.section.push(controlResultSection);
            let riskDifference = NaN;
            let riskDifferenceDisplay = '[Not a proportion.]';
            let interventionEventRate = NaN;
            let interventionEventRateDisplay = '[Not a proportion.]';
            let entryArray;
            let riskDifferenceLowValue = NaN;
            let riskDifferenceLowValueDisplay = 'No lower bound';
            let riskDifferenceHighValue = NaN;
            let riskDifferenceHighValueDisplay = 'No upper bound';
            if (comparativeEvidenceJson) {
                let InterventionVsControlEvidence = comparativeEvidenceJson;
                entryArray = [
                    {
                        'reference': 'Evidence/' + InterventionVsControlEvidence.id,
                        'type': 'Evidence',
                        'display': (InterventionVsControlEvidence.title || InterventionVsControlEvidence.name || "")
                    },
                    comparatorOnlyEvidenceJsonEntry
                ];
                if (InterventionVsControlEvidence.statistic) {
                    let riskDifferenceStatistic;
                    let riskRatioStatistic;
                    let oddsRatioStatistic;
                    for (const stat of InterventionVsControlEvidence.statistic) {
                        if (stat.statisticType?.coding?.length > 0 && stat.quantity && 
                            stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                            if (codeMatchesRiskDifference(stat.statisticType.coding[0].code)) {
                                riskDifferenceStatistic = stat;
                            }
                        if (codeMatchesRelativeRisk(stat.statisticType.coding[0].code) || //risk ratio
                                stat.statisticType.coding[0].code === 'STATO:0000677' || stat.statisticType.coding[0].code === 'TBD:0000026' || //hazard ratio currently treated like risk ratio
                                stat.statisticType.coding[0].code === 'STATO:0000678' || stat.statisticType.coding[0].code === 'TBD:0000028' || //prevalence ratio currently treated like risk ratio
                                stat.statisticType.coding[0].code === 'STATO:0000680' || stat.statisticType.coding[0].code === 'TBD:0000027' //incidence rate ratio currently treated like risk ratio
                            ) {
                                riskRatioStatistic = stat;
                            }
                        if (stat.statisticType.coding[0].code === 'STATO:0000182' || stat.statisticType.coding[0].code === 'C16932') {
                                oddsRatioStatistic = stat;
                            }
                        }
                    }
                    if (oddsRatioStatistic) {
                        let oddsRatio = oddsRatioStatistic.quantity.value;
                        let controlOdds = controlEventRate / (100 - controlEventRate);
                        let interventionOdds = controlOdds * oddsRatio;
                        let interventionEventRate = (interventionOdds / (interventionOdds + 1)) * 100;
                        interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                        riskDifference = interventionEventRate - controlEventRate;
                        riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                        if (oddsRatioStatistic.attributeEstimate?.length > 0) {
                            for (const attributeEstimate of oddsRatioStatistic.attributeEstimate) {
                                if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                    if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                        let lowORValue = Number(attributeEstimate.range.low.value);
                                        let interventionOddsLow = controlOdds * lowORValue;
                                        let interventionEventRateLow = (interventionOddsLow / (interventionOddsLow + 1)) * 100;
                                        riskDifferenceLowValue = interventionEventRateLow - controlEventRate;
                                        riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                    }
                                    if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                        let highRRValue = Number(attributeEstimate.range.high.value);
                                        let interventionOddsHigh = controlOdds * highRRValue;
                                        let interventionEventRateHigh = (interventionOddsHigh / (interventionOddsHigh + 1)) * 100;
                                        riskDifferenceHighValue = interventionEventRateHigh - controlEventRate;
                                        riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                    }
                                    let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                    riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                }
                            }
                        }
                    } else if (riskRatioStatistic) {
                        let relativeRisk = riskRatioStatistic.quantity.value;
                        let interventionEventRate = controlEventRate * relativeRisk;
                        interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                        riskDifference = interventionEventRate - controlEventRate;
                        riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                        if (riskRatioStatistic.attributeEstimate?.length > 0) {
                            for (const attributeEstimate of riskRatioStatistic.attributeEstimate) {
                                if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                    if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                        let lowRRValue = Number(attributeEstimate.range.low.value);
                                        riskDifferenceLowValue = (controlEventRate * lowRRValue) - controlEventRate;
                                        riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                    }
                                    if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                        let highRRValue = Number(attributeEstimate.range.high.value);
                                        riskDifferenceHighValue = (controlEventRate * highRRValue) - controlEventRate;
                                        riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                    }
                                    let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                    riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                }
                            }
                        }
                    } else if (riskDifferenceStatistic) {
                        riskDifference = riskDifferenceStatistic.quantity.value * 100;
                        if (riskDifferenceStatistic.quantity.unit === "%" || riskDifferenceStatistic.quantity.code === "%") {
                            riskDifference = riskDifferenceStatistic.quantity.value;
                        }
                        riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                        interventionEventRate = controlEventRate + riskDifference;
                        interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                        if (riskDifferenceStatistic.attributeEstimate?.length > 0) {
                            for (const attributeEstimate of riskDifferenceStatistic.attributeEstimate) {
                                if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                    if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                        riskDifferenceLowValue = Number(attributeEstimate.range.low.value) * 100;
                                        if (attributeEstimate.range.low.unit === "%" || attributeEstimate.range.low.code === "%") {
                                            riskDifferenceLowValue = Number(attributeEstimate.range.low.value);
                                        }
                                        riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                    }
                                    if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                        riskDifferenceHighValue = Number(attributeEstimate.range.high.value) * 100;
                                        if (attributeEstimate.range.high.unit === "%" || attributeEstimate.range.high.code === "%") {
                                            riskDifferenceHighValue = Number(attributeEstimate.range.high.value);
                                        }
                                        riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                    }
                                    let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                    riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                }
                            }
                        }
                    }

                    if (isNaN(riskDifference) && !isNaN(controlEventRate)) {
                        for (const stat of InterventionVsControlEvidence.statistic) {
                            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 && stat.statisticType.coding[0].code) {
                                const statCode = stat.statisticType.coding[0].code;
                                if (checkIfStatisticAbsoluteOrRelative(statCode) === "absolute") {
                                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                        let absoluteDifference = stat.quantity.value * 100;
                                        if (stat.quantity.unit === "%" || stat.quantity.code === "%") {
                                            absoluteDifference = stat.quantity.value;
                                        }
                                        interventionEventRate = controlEventRate + absoluteDifference;
                                        interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                                        riskDifference = absoluteDifference;
                                        riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                                        if (stat.attributeEstimate?.length > 0) {
                                            for (const attributeEstimate of stat.attributeEstimate) {
                                                if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                                    if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                                        riskDifferenceLowValue = Number(attributeEstimate.range.low.value) * 100;
                                                        if (attributeEstimate.range.low.unit === "%" || attributeEstimate.range.low.code === "%") {
                                                            riskDifferenceLowValue = Number(attributeEstimate.range.low.value);
                                                        }
                                                        riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                                    }
                                                    if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                                        riskDifferenceHighValue = Number(attributeEstimate.range.high.value) * 100;
                                                        if (attributeEstimate.range.high.unit === "%" || attributeEstimate.range.high.code === "%") {
                                                            riskDifferenceHighValue = Number(attributeEstimate.range.high.value);
                                                        }
                                                        riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                                    }
                                                    let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                                    riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                                }
                                            }
                                        }
                                    }

                                } else if (checkIfStatisticAbsoluteOrRelative(statCode) === "relative") {
                                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                        let relativeDifference = stat.quantity.value;
                                        interventionEventRate = controlEventRate * relativeDifference;
                                        interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                                        riskDifference = interventionEventRate - controlEventRate;
                                        riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                                        if (stat.attributeEstimate?.length > 0) {
                                            for (const attributeEstimate of stat.attributeEstimate) {
                                                if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                                    if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                                        let lowRRValue = Number(attributeEstimate.range.low.value);
                                                        riskDifferenceLowValue = (controlEventRate * lowRRValue) - controlEventRate;
                                                        riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                                    }
                                                    if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                                        let highRRValue = Number(attributeEstimate.range.high.value);
                                                        riskDifferenceHighValue = (controlEventRate * highRRValue) - controlEventRate;
                                                        riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                                    }
                                                    let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                                    riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            let treatmentResultSection = {
                "title": "Result With Treatment--Derived",
                "code": {
                    "coding": [
                        {
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "result-with-intervention-alone-calculated",
                            "display": "Result with intervention alone (calculated)"
                        }
                    ],
                    "text": "result with intervention with calculated value derived from the control group and effect estimate"
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + interventionEventRateDisplay + "</p></div>"
                }
            };
            if (entryArray) {
                treatmentResultSection["entry"] = entryArray;
            }
            outcomeSection.section.push(treatmentResultSection);

            let effectEstimateSection = {
                "title": "Effect Estimate",
                "code": {
                    "coding": [
                        {
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "result-with-intervention-vs-comparator",
                            "display": "Result with intervention vs. comparator"
                        }
                    ]
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + riskDifferenceDisplay + "</p></div>"
                }
            };
            if (entryArray) {
                effectEstimateSection["entry"] = entryArray;
            }
            outcomeSection.section.push(effectEstimateSection);

            let netEffectContributionSection = {
                "title": "Net Effect Contribution",
                "code": {
                    "coding": [
                        {
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "net-effect-contribution",
                            "display": "Net Effect Contribution"
                        }
                    ],
                    "text": "Net Effect Contribution"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[Not yet calculated.]</p></div>"
                }
            }

            outcomeSection.section.push(netEffectContributionSection);

            newSectionForComposition[1].section.push(outcomeSection);
        }
    }
    addElementValues.section = newSectionForComposition;

    return addElementValues;
};

export default addElementValuesForSummaryOfNetEffectFromComparativeEvidenceSynthesisReport;